import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string-for-all';
import { AppContext } from '@prisjakt/ui-app-context';
import Dropdown from '@prisjakt/ui-dropdown-new';
import DropdownItem from '@prisjakt/ui-dropdown-new/src/item';
import translator from '@prisjakt/ui-translate/src/translator';
import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';
import { getSortedQuery } from '@prisjakt/product-collection-urls';
import { useViewportContext } from '../../../../components/viewport/context';
import { trackProductCollectionSortUpdate } from '../../tracking';
import { getPropertyFromSortKey } from '../../utils/properties';

import * as S from './styles';

type Props = {
  availableSorts: string[];
  availableSortsTranslations: Record<string, string>;
  currentSort: string;
};

const getTranslationForSortOption = (
  sortKey: string,
  availableTranslations: Record<string, string>,
  t: ReturnType<typeof translator>
): string => {
  const property = getPropertyFromSortKey(sortKey);
  return availableTranslations[property]
    ? availableTranslations[property]
    : t('common', `sort.${sortKey}` as keyof ParamsAndReturnTypes['common']);
};

const SortOptions = ({
  availableSorts,
  availableSortsTranslations,
  currentSort,
}: Props) => {
  const { t } = useContext(AppContext);
  const { isMobile } = useViewportContext();
  const location = useLocation();
  const sortOptions = Array.isArray(availableSorts) ? availableSorts : [];
  const currentSortName = getTranslationForSortOption(
    currentSort,
    availableSortsTranslations,
    t
  );
  if (!sortOptions.length) {
    return null;
  }

  return (
    <S.SortOptions>
      <S.SortOptionsTitle>{t('common', 'sort.title')}:</S.SortOptionsTitle>

      <Dropdown
        variant="secondary"
        showArrow
        grow={isMobile ? 'right' : 'left'}
        toggleElement={
          <S.SortOptionsButton variant="text" color="primary">
            {currentSortName}
          </S.SortOptionsButton>
        }
        data-test="SortOrderDropdown"
      >
        {sortOptions
          .filter(sortOption => sortOption !== currentSort)
          .map(sort => {
            const queryParams = getSortedQuery({
              ...queryString.parse(location.search.replace(/^\?/, '')),
              offset: undefined,
              direction: undefined,
              sort,
            });

            return (
              <DropdownItem
                key={sort}
                to={`${location.pathname}?${queryString.stringify(
                  queryParams
                )}`}
                onClick={() => trackProductCollectionSortUpdate(sort)}
              >
                {getTranslationForSortOption(
                  sort,
                  availableSortsTranslations,
                  t
                )}
              </DropdownItem>
            );
          })}
      </Dropdown>
    </S.SortOptions>
  );
};

export default SortOptions;
